/**
 * StoccaggioModal (#91/#93) — transizione attesa_compliance → in_stoccaggio.
 * Conferma registrazione Ecomatico + area effettiva. Muove il saldo area (+).
 * Capienza runtime: blocco con override esplicito (capacità NULL → nessun
 * vincolo, gestito dal BE che non solleva override_richiesto).
 */
import { useState, useEffect } from 'react';
import { PackageCheck, X } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import axios from 'axios';
import { inStoccaggio } from '../../api/conferimenti';
import type { ConferimentoDetail } from '../../api/conferimenti';
import { listAreas } from '../../api/areas';
import type { Area } from '../../api/areas';
import i18n from '../../i18n';
import { fmtKg } from '../../lib/saldo';

const schema = z.object({
  area_effettiva_id: z.string().min(1, i18n.t('conferimenti.stoccaggio_modal.val_area')),
  ecomatico_ok: z.literal(true, { message: i18n.t('conferimenti.stoccaggio_modal.val_ecomatico') }),
});
type FormData = z.infer<typeof schema>;

const inputCls = 'w-full text-sm text-ink-dark border border-stone-border rounded px-2 py-1.5 focus:outline-none focus:border-sage focus:ring-1 focus:ring-sage/20 bg-white';

interface Props {
  conferimentoId: string;
  numero: string | null;
  areaTentativaId: string | null;
  pesoNettoKg: number | null;
  cerCode: string | null;
  cerAttivoId: string | null;
  onClose: () => void;
  onSaved: (updated: ConferimentoDetail) => void;
}

export default function StoccaggioModal({ conferimentoId, numero, areaTentativaId, pesoNettoKg, cerCode, cerAttivoId, onClose, onSaved }: Props) {
  const { t } = useTranslation();
  const [areas, setAreas] = useState<Area[]>([]);
  const [serverError, setServerError] = useState<string | null>(null);
  const [overridePrompt, setOverridePrompt] = useState<{ message: string; peso_attuale: number; peso_aggiunto: number; capacita: number } | null>(null);
  const [pendingAreaId, setPendingAreaId] = useState<string | null>(null);

  useEffect(() => {
    listAreas().then(setAreas).catch(() => {});
  }, []);

  // B2 (#4, V48): ingresso solo in R13 che AMMETTONO la riga CER dell'ordine
  // (matching per id sul ponte, come aree-disponibili — il gemello stesso codice
  // è escluso). Ordine senza riga CER (legacy) → tutte le R13, con warning.
  const areeAmmesse = areas.filter(
    (a) => a.categoria === 'R13' && (!cerAttivoId || a.materiali_ammessi.some((m) => m.id === cerAttivoId)),
  );

  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm({
    resolver: zodResolver(schema),
    defaultValues: { area_effettiva_id: areaTentativaId ?? '' },
  });

  async function submit(areaId: string, override = false) {
    setServerError(null);
    try {
      const updated = await inStoccaggio(conferimentoId, { area_effettiva_id: areaId, override_capacita: override });
      onSaved(updated);
    } catch (err: unknown) {
      if (axios.isAxiosError(err) && err.response?.status === 422) {
        const detail = err.response.data?.detail;
        if (detail && typeof detail === 'object' && detail.override_richiesto) {
          setOverridePrompt({
            message: detail.message,
            peso_attuale: detail.peso_attuale_kg,
            peso_aggiunto: detail.peso_aggiunto_kg,
            capacita: detail.capacita_kg,
          });
          setPendingAreaId(areaId);
          return;
        }
        setServerError(typeof detail === 'string' ? detail : t('conferimenti.stoccaggio_modal.errore_validazione'));
      } else {
        setServerError(t('conferimenti.stoccaggio_modal.errore_salvataggio'));
      }
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-mid">
          <h2 className="text-base font-semibold text-ink-dark flex items-center gap-2">
            <PackageCheck size={16} className="text-sage" /> {t('conferimenti.stoccaggio_modal.titolo')}
          </h2>
          <button type="button" onClick={onClose} className="text-ink-soft hover:text-ink-dark"><X size={18} /></button>
        </div>

        {overridePrompt ? (
          <div className="px-6 py-5 space-y-4">
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm font-medium text-amber-800">{overridePrompt.message}</p>
              <p className="text-xs text-amber-700 mt-1">
                {t('conferimenti.stoccaggio_modal.override_dettaglio', {
                  attuale: fmtKg(overridePrompt.peso_attuale),
                  ingresso: fmtKg(overridePrompt.peso_aggiunto),
                  capacita: fmtKg(overridePrompt.capacita),
                })}
              </p>
            </div>
            <p className="text-sm text-ink-mid">{t('conferimenti.stoccaggio_modal.override_domanda')}</p>
            <div className="flex gap-3">
              <button type="button" onClick={() => setOverridePrompt(null)} className="flex-1 px-4 py-2 text-sm font-medium text-ink-mid border border-stone-border rounded-lg hover:bg-stone-bg transition-colors">{t('common.azioni.annulla')}</button>
              <button type="button" onClick={() => { const a = pendingAreaId; setOverridePrompt(null); if (a) void submit(a, true); }} className="flex-1 px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700 transition-colors">{t('conferimenti.stoccaggio_modal.conferma_override')}</button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit((d) => submit((d as FormData).area_effettiva_id))}>
            <div className="px-6 py-5 space-y-4">
              <p className="text-xs text-ink-soft">
                <Trans i18nKey="conferimenti.stoccaggio_modal.riepilogo"
                  values={{ numero: numero ?? '—', netto: pesoNettoKg != null ? `${fmtKg(pesoNettoKg)} kg` : '—' }}
                  components={{ strong: <strong /> }} />
              </p>
              <div>
                <label className="block text-xs font-medium text-ink-soft mb-1">
                  {t('conferimenti.stoccaggio_modal.area_label')}{cerAttivoId && <span className="text-ink-faint font-normal"> {t('conferimenti.stoccaggio_modal.area_hint')}{cerCode ? ` ${t('conferimenti.stoccaggio_modal.area_cer', { cer: cerCode })}` : ''}</span>}
                </label>
                <select className={`${inputCls} cursor-pointer`} {...register('area_effettiva_id')}>
                  <option value="">{t('conferimenti.stoccaggio_modal.area_seleziona')}</option>
                  {areeAmmesse.map((a) => (
                    <option key={a.id} value={a.id}>{a.codice} — {a.descrizione}</option>
                  ))}
                </select>
                {!cerAttivoId && (
                  <p className="text-xs text-amber-600 mt-1">{t('conferimenti.stoccaggio_modal.no_cer_warning')}</p>
                )}
                {areeAmmesse.length === 0 && (
                  <p className="text-xs text-amber-600 mt-1">{t('conferimenti.stoccaggio_modal.no_aree_warning')}</p>
                )}
                {errors.area_effettiva_id && <p className="text-xs text-accent-danger mt-1">{errors.area_effettiva_id.message}</p>}
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="ecomatico" className="w-4 h-4 text-sage rounded border-stone-border" {...register('ecomatico_ok')} />
                <label htmlFor="ecomatico" className="text-sm text-ink-mid">{t('conferimenti.stoccaggio_modal.ecomatico_confermato')}</label>
              </div>
              {errors.ecomatico_ok && <p className="text-xs text-accent-danger">{errors.ecomatico_ok.message}</p>}
              {serverError && (
                <div className="px-3 py-2 bg-accent-danger-bg border border-[#FECACA] rounded text-sm text-accent-danger">{serverError}</div>
              )}
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-stone-mid bg-stone-bg">
              <button type="button" onClick={onClose} disabled={isSubmitting} className="px-4 py-2 text-sm font-medium text-ink-mid border border-stone-border rounded-lg hover:bg-white transition-colors disabled:opacity-50">{t('common.azioni.annulla')}</button>
              <button type="submit" disabled={isSubmitting} className="px-4 py-2 text-sm font-medium text-white bg-sage rounded-lg hover:bg-sage-dark transition-colors disabled:opacity-60">
                {isSubmitting ? t('conferimenti.stoccaggio_modal.salvataggio') : t('conferimenti.stoccaggio_modal.conferma')}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
